import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";

/* ─── Returns Calculator ──────────────────────────────────── */
function ReturnsCalculator() {
  const [amount, setAmount] = useState("50000");
  const raw = parseFloat(amount.replace(/,/g, "")) || 0;
  const annual = raw * 0.1;
  const monthly = annual / 12;
  const fiveYear = annual * 5;
  const total = raw + fiveYear;

  const fmt = (n: number) =>
    "£" + Math.round(n).toLocaleString("en-GB");

  return (
    <div className="bg-[#071A2E] rounded-2xl overflow-hidden">
      <div className="px-6 py-5 border-b border-white/10">
        <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.3em] mb-1">Live Returns Calculator</p>
        <h3 className="text-white text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
          What Could Your Money Earn?
        </h3>
        <p className="text-slate-400 text-xs mt-1">Illustrative only. Based on 10.00% p.a. target. Not guaranteed.</p>
      </div>
      <div className="p-6">
        <label className="block text-slate-400 text-xs font-semibold uppercase tracking-wide mb-2">
          Your Investment Amount
        </label>
        <div className="relative mb-6">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C9A84C] font-bold text-lg">£</span>
          <input
            type="number"
            min={10000}
            step={1000}
            value={amount}
            onChange={e => setAmount(e.target.value)}
            className="w-full bg-white/8 border border-white/15 rounded-xl pl-9 pr-4 py-3.5 text-white text-lg font-bold focus:outline-none focus:border-[#C9A84C]/60 transition-colors"
            placeholder="10000"
          />
        </div>

        {raw < 10000 && raw > 0 && (
          <p className="text-amber-400 text-xs mb-4">Minimum investment is £10,000</p>
        )}

        <div className="grid grid-cols-2 gap-3 mb-4">
          {[
            { label: "Monthly Income", value: fmt(monthly), highlight: true },
            { label: "Annual Income", value: fmt(annual), highlight: false },
            { label: "5-Year Income", value: fmt(fiveYear), highlight: false },
            { label: "Total at Maturity", value: fmt(total), highlight: false },
          ].map(item => (
            <div
              key={item.label}
              className={`rounded-xl p-4 ${item.highlight ? "bg-[#C9A84C]/15 border border-[#C9A84C]/30" : "bg-white/6 border border-white/8"}`}
            >
              <p className={`text-xs font-semibold mb-1 ${item.highlight ? "text-[#C9A84C]" : "text-slate-400"}`}>
                {item.label}
              </p>
              <p className={`font-bold leading-none ${item.highlight ? "text-[#C9A84C] text-xl" : "text-white text-base"}`}
                style={{ fontFamily: "'Playfair Display', serif" }}>
                {raw >= 10000 ? item.value : "—"}
              </p>
            </div>
          ))}
        </div>

        {/* Quick amount buttons */}
        <div className="flex flex-wrap gap-2">
          {["10000", "25000", "50000", "100000", "250000"].map(v => (
            <button
              key={v}
              onClick={() => setAmount(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                amount === v
                  ? "bg-[#C9A84C] text-[#071A2E]"
                  : "bg-white/8 text-slate-400 hover:bg-white/15 hover:text-white"
              }`}
            >
              £{parseInt(v).toLocaleString("en-GB")}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Express Interest Form ───────────────────────────────── */
function ExpressInterestForm({ onClose }: { onClose: () => void }) {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("sending");
    const form = e.currentTarget;
    const data = new URLSearchParams();
    Array.from(form.elements).forEach(el => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name) {
        if (input.type === "checkbox") {
          if ((input as HTMLInputElement).checked) data.append(input.name, input.value);
        } else {
          data.append(input.name, input.value);
        }
      }
    });
    try {
      const res = await fetch("https://readdy.ai/api/form/d7p2cspus6nbnotnp6vg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("sent");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(7,26,46,0.85)" }}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-[#071A2E] rounded-t-2xl px-6 py-5 flex items-center justify-between">
          <div>
            <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.3em]">Thorpe Dellow</p>
            <h3 className="text-white font-bold text-base" style={{ fontFamily: "'Playfair Display', serif" }}>
              Express Interest
            </h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line leading-none" />
          </button>
        </div>

        {status === "sent" ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
              <i className="ri-check-line text-green-600 text-2xl leading-none" />
            </div>
            <h4 className="text-[#071A2E] font-bold text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Thank You
            </h4>
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              Your enquiry has been received. A member of our investment team will contact you within one business day.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl bg-[#071A2E] text-white text-sm font-bold cursor-pointer hover:bg-[#0D2B4A] transition-colors whitespace-nowrap"
            >
              Close
            </button>
          </div>
        ) : (
          <form data-readdy-form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-600 text-xs font-semibold mb-1.5">First Name *</label>
                <input
                  name="first_name"
                  required
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-[#071A2E] focus:outline-none focus:border-[#C9A84C] transition-colors"
                  placeholder="James"
                />
              </div>
              <div>
                <label className="block text-slate-600 text-xs font-semibold mb-1.5">Last Name *</label>
                <input
                  name="last_name"
                  required
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-[#071A2E] focus:outline-none focus:border-[#C9A84C] transition-colors"
                  placeholder="Smith"
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-600 text-xs font-semibold mb-1.5">Email Address *</label>
              <input
                name="email"
                type="email"
                required
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-[#071A2E] focus:outline-none focus:border-[#C9A84C] transition-colors"
                placeholder="james@example.com"
              />
            </div>

            <div>
              <label className="block text-slate-600 text-xs font-semibold mb-1.5">Phone Number</label>
              <input
                name="phone"
                type="tel"
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-[#071A2E] focus:outline-none focus:border-[#C9A84C] transition-colors"
                placeholder="+44 7700 000000"
              />
            </div>

            <div>
              <label className="block text-slate-600 text-xs font-semibold mb-1.5">Investment Amount</label>
              <select
                name="investment_amount"
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-[#071A2E] focus:outline-none focus:border-[#C9A84C] transition-colors bg-white cursor-pointer"
              >
                <option value="">Select a range</option>
                <option value="£10,000 – £24,999">£10,000 – £24,999</option>
                <option value="£25,000 – £49,999">£25,000 – £49,999</option>
                <option value="£50,000 – £99,999">£50,000 – £99,999</option>
                <option value="£100,000 – £249,999">£100,000 – £249,999</option>
                <option value="£250,000+">£250,000+</option>
              </select>
            </div>

            <div>
              <label className="block text-slate-600 text-xs font-semibold mb-1.5">Investor Classification *</label>
              <select
                name="investor_type"
                required
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-[#071A2E] focus:outline-none focus:border-[#C9A84C] transition-colors bg-white cursor-pointer"
              >
                <option value="">Select classification</option>
                <option value="Certified High Net Worth Individual">Certified High Net Worth Individual</option>
                <option value="Self-Certified Sophisticated Investor">Self-Certified Sophisticated Investor</option>
              </select>
            </div>

            <div>
              <label className="block text-slate-600 text-xs font-semibold mb-1.5">Message</label>
              <textarea
                name="message"
                rows={3}
                maxLength={500}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-[#071A2E] focus:outline-none focus:border-[#C9A84C] transition-colors resize-none"
                placeholder="Any questions or specific requirements..."
              />
            </div>

            <div className="flex items-start gap-2.5">
              <input
                type="checkbox"
                name="consent"
                id="consent"
                value="yes"
                required
                className="mt-0.5 cursor-pointer flex-shrink-0"
              />
              <label htmlFor="consent" className="text-slate-500 text-xs leading-relaxed cursor-pointer">
                I confirm I am a Certified High Net Worth Individual or Self-Certified Sophisticated Investor and consent to being contacted by Thorpe Dellow regarding this investment product.
              </label>
            </div>

            <p className="text-slate-400 text-[10px] leading-relaxed">
              Capital is at risk. This is not financial advice. Thorpe Property Development Ltd (Co. No. 07590563). Section 21 FSMA 2000.
            </p>

            {status === "error" && (
              <p className="text-red-500 text-xs">Something went wrong. Please try again or email invest@thorpedellow.com</p>
            )}

            <button
              type="submit"
              disabled={status === "sending"}
              className="w-full py-3.5 rounded-xl font-bold text-sm text-[#071A2E] cursor-pointer whitespace-nowrap transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E2C27A 50%, #C9A84C 100%)" }}
            >
              {status === "sending" ? "Sending..." : "Submit Enquiry"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ─── Floating TOC ────────────────────────────────────────── */
const tocItems = [
  { id: "cover", label: "Cover" },
  { id: "overview", label: "01 Overview" },
  { id: "product", label: "02 Product" },
  { id: "steps", label: "02 How It Works" },
  { id: "returns", label: "03 Returns" },
  { id: "portfolio", label: "04 Portfolio" },
  { id: "security", label: "05 Security" },
  { id: "team", label: "06 Team" },
  { id: "contact", label: "07 Contact" },
];

function FloatingTOC() {
  const [active, setActive] = useState("cover");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => {
      const sections = tocItems.map(t => document.getElementById(t.id));
      let current = "cover";
      sections.forEach(sec => {
        if (sec && sec.getBoundingClientRect().top <= 120) {
          current = sec.id;
        }
      });
      setActive(current);
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <>
      {/* Desktop sidebar */}
      <div className="print:hidden fixed left-4 top-1/2 -translate-y-1/2 z-40 hidden xl:flex flex-col gap-1.5">
        {tocItems.map(t => (
          <button
            key={t.id}
            onClick={() => scrollTo(t.id)}
            title={t.label}
            className={`group flex items-center gap-2 cursor-pointer transition-all`}
          >
            <div className={`h-[3px] rounded-full transition-all ${active === t.id ? "w-6 bg-[#C9A84C]" : "w-3 bg-slate-300 group-hover:w-5 group-hover:bg-[#C9A84C]/60"}`} />
            <span className={`text-[10px] font-semibold whitespace-nowrap transition-all ${active === t.id ? "text-[#071A2E] opacity-100" : "text-slate-400 opacity-0 group-hover:opacity-100"}`}>
              {t.label}
            </span>
          </button>
        ))}
      </div>

      {/* Mobile FAB */}
      <div className="print:hidden fixed bottom-6 right-6 z-40 xl:hidden">
        <button
          onClick={() => setOpen(o => !o)}
          className="w-12 h-12 flex items-center justify-center rounded-full bg-[#071A2E] border border-[#C9A84C]/40 cursor-pointer"
          style={{ boxShadow: "0 4px 20px rgba(7,26,46,0.3)" }}
        >
          <i className={`${open ? "ri-close-line" : "ri-list-check"} text-[#C9A84C] text-lg leading-none`} />
        </button>
        {open && (
          <div className="absolute bottom-14 right-0 bg-white rounded-2xl border border-slate-100 py-2 w-48" style={{ boxShadow: "0 8px 32px rgba(7,26,46,0.15)" }}>
            {tocItems.map(t => (
              <button
                key={t.id}
                onClick={() => scrollTo(t.id)}
                className={`w-full text-left px-4 py-2.5 text-xs font-semibold transition-colors cursor-pointer ${active === t.id ? "text-[#C9A84C] bg-[#C9A84C]/8" : "text-slate-600 hover:bg-slate-50"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/* ─── Main Page ───────────────────────────────────────────── */
export default function BrochurePage() {
  const [showForm, setShowForm] = useState(false);
  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-white font-sans">

      {showForm && <ExpressInterestForm onClose={() => setShowForm(false)} />}
      <FloatingTOC />

      {/* ── Toolbar ── */}
      <div className="print:hidden sticky top-0 z-50 bg-white border-b border-slate-100" style={{ boxShadow: "0 1px 10px rgba(7,26,46,0.06)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-[#071A2E] text-xs font-medium transition-colors cursor-pointer whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line leading-none" /></div>
              Back
            </Link>
            <span className="text-slate-200 hidden sm:inline">|</span>
            <span className="text-xs text-slate-400 hidden sm:inline">Investment Brochure — April 2026</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#C9A84C]/50 text-[#C9A84C] text-xs font-bold hover:bg-[#C9A84C]/8 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line leading-none" /></div>
              Express Interest
            </button>
            <button onClick={handlePrint} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#071A2E] text-white text-xs font-bold hover:bg-[#0D2B4A] transition-colors cursor-pointer whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line leading-none" /></div>
              Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          COVER
      ══════════════════════════════════════════ */}
      <div id="cover" className="relative bg-[#071A2E] overflow-hidden" style={{ minHeight: "100vh" }}>
        <div className="absolute inset-0" style={{
          backgroundImage: "url('https://readdy.ai/api/search-image?query=aerial%20view%20UK%20social%20housing%20assisted%20living%20properties%20Yorkshire%20England%20golden%20afternoon%20light%20drone%20photography%20residential%20care%20homes%20neat%20gardens%20British%20architecture%20cinematic%20wide%20shot%20warm%20tones&width=1400&height=900&seq=br001&orientation=landscape')",
          backgroundSize: "cover", backgroundPosition: "center top"
        }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#071A2E]/85 via-[#071A2E]/75 to-[#071A2E]/92" />
        <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: "linear-gradient(90deg, transparent, #C9A84C 30%, #E8C56A 50%, #C9A84C 70%, transparent)" }} />

        <div className="relative z-10 max-w-5xl mx-auto px-6 sm:px-10 flex flex-col" style={{ minHeight: "100vh", paddingTop: 80, paddingBottom: 60 }}>
          <div className="flex items-center justify-between mb-auto">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl border border-[#C9A84C]/40" style={{ background: "rgba(201,168,76,0.12)" }}>
                <i className="ri-home-4-fill text-[#C9A84C] text-lg leading-none" />
              </div>
              <div>
                <p className="font-bold text-sm tracking-widest uppercase text-white leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>Thorpe Dellow</p>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#C9A84C]/70 mt-0.5">Structured Investments</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-white/40 text-[10px] uppercase tracking-widest">Investment Brochure</p>
              <p className="text-white/60 text-xs mt-0.5">April 2026</p>
            </div>
          </div>

          <div className="py-16 md:py-20">
            <div className="w-10 h-[2px] mb-8" style={{ background: "linear-gradient(90deg, #C9A84C, #E2C27A)" }} />
            <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-[0.3em] mb-5">Social Housing Structured Product</p>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white leading-[1.05] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              UK Social Housing<br /><span style={{ color: "#C9A84C" }}>Property Portfolio</span>
            </h1>
            <p className="text-white/65 text-base leading-relaxed mb-10 max-w-2xl">
              A specialist structured investment product backed by a diversified portfolio of UK assisted living assets — targeting <strong className="text-white">10.00% per annum</strong>, paid monthly to investors.
            </p>
            <div className="flex flex-wrap gap-3 mb-10">
              {[
                { v: "10.00% p.a.", l: "Target Return" },
                { v: "5 Years", l: "Investment Term" },
                { v: "35+", l: "Underlying Assets" },
                { v: "£47M+", l: "Combined AUM" },
              ].map(s => (
                <div key={s.l} className="bg-white/8 border border-white/15 rounded-xl px-5 py-3 backdrop-blur-sm">
                  <p className="text-white font-bold text-lg leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>{s.v}</p>
                  <p className="text-white/45 text-[10px] mt-1 uppercase tracking-wide">{s.l}</p>
                </div>
              ))}
            </div>

            {/* Calculator on cover */}
            <div className="max-w-lg">
              <ReturnsCalculator />
            </div>
          </div>

          <div className="border-t border-white/10 pt-5">
            <p className="text-white/30 text-[10px] leading-relaxed">
              This document is a marketing communication directed solely at Certified HNW Individuals and Sophisticated Investors. Capital is at risk. Past performance is not indicative of future results. Section 21 FSMA 2000.
            </p>
            <p className="text-white/20 text-[10px] mt-1">Thorpe Property Development Ltd (Co. No. 07590563) · One Embankment, Leeds LS1 4DW · Section 21 FSMA 2000 · Capital at risk</p>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          SECTION 01 — INVESTMENT OVERVIEW
      ══════════════════════════════════════════ */}
      <div id="overview" className="bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-20">
          <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.3em] mb-3">Section 01</p>
          <h2 className="text-4xl font-bold text-[#071A2E] mb-10" style={{ fontFamily: "'Playfair Display', serif" }}>Investment Overview</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-14">
            <div>
              <p className="text-slate-600 text-sm leading-relaxed mb-5">
                What if your money could do two things at once — earn a reliable 10% each year and help house thousands of vulnerable people across the UK? That's exactly what Thorpe Dellow was built to do.
              </p>
              <p className="text-slate-600 text-sm leading-relaxed mb-8">
                Established in Leeds in June 2005, Thorpe Dellow has spent two decades doing one thing exceptionally well: giving qualified investors access to the stable, government-supported income that flows from UK social housing. The model is simple. The track record is not. Our investors have never missed a single monthly payment.
              </p>
              <div className="bg-[#F5F3EE] rounded-2xl p-6 mb-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#071A2E]">
                    <i className="ri-government-line text-[#C9A84C] text-base leading-none" />
                  </div>
                  <h3 className="text-sm font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>Government-Backed Income Stream</h3>
                </div>
                <p className="text-slate-600 text-xs leading-relaxed">
                  Every lease in this portfolio is underpinned by housing benefit — a statutory government obligation, not a discretionary budget line. It cannot be frozen, withdrawn, or deferred by market conditions. That makes the income reaching your account categorically different from anything tied to commercial rents or equity dividends.
                </p>
              </div>
              <div className="bg-[#F5F3EE] rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#071A2E]">
                    <i className="ri-shield-check-line text-[#C9A84C] text-base leading-none" />
                  </div>
                  <h3 className="text-sm font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>First Legal Charge Security</h3>
                </div>
                <p className="text-slate-600 text-xs leading-relaxed">
                  Your investment is secured by a first legal charge over each underlying property, held by an Independent Security Trustee who works exclusively for noteholders — not for Thorpe Dellow. In any default scenario, your claim ranks ahead of the company's own creditors. That isn't a reassurance. It's a legal fact.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: "ri-percent-line", v: "10.00%", l: "Target Return", sub: "Paid monthly to investors" },
                { icon: "ri-calendar-2-line", v: "5 Years", l: "Investment Term", sub: "Fixed structured note" },
                { icon: "ri-building-4-line", v: "35+", l: "Underlying Assets", sub: "UK social housing props" },
                { icon: "ri-money-pound-circle-line", v: "£47M+", l: "Combined AUM", sub: "Assets under management" },
              ].map(h => (
                <div key={h.l} className="bg-[#071A2E] rounded-2xl p-6 flex flex-col justify-between" style={{ minHeight: 140 }}>
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#C9A84C]/15 mb-4">
                    <i className={`${h.icon} text-[#C9A84C] text-base leading-none`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white leading-none mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{h.v}</p>
                    <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-wide">{h.l}</p>
                    <p className="text-white/40 text-[10px] mt-0.5">{h.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="border-t border-slate-100 pt-5">
            <p className="text-slate-300 text-[10px]">Returns are targets only. Capital at risk. Past performance is not indicative of future results. For Certified HNW Individuals and Sophisticated Investors · Thorpe Property Development Ltd (Co. No. 07590563) · One Embankment, Leeds LS1 4DW · Section 21 FSMA 2000 · Capital at risk</p>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          SECTION 02 — THE STRUCTURED PRODUCT
      ══════════════════════════════════════════ */}
      <div id="product" className="bg-[#F5F3EE]">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-20">
          <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.3em] mb-3">Section 02</p>
          <h2 className="text-4xl font-bold text-[#071A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>The Structured Product</h2>
          <p className="text-slate-600 text-sm leading-relaxed mb-10 max-w-2xl">
            Most investments make you wait until the end to see your money. This one pays you every single month — for five straight years.
          </p>
          <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 mb-8">
            <div className="bg-[#071A2E] px-6 py-4">
              <p className="text-white font-bold text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>Key Terms at a Glance</p>
            </div>
            {[
              ["Target Return", "10.00% per annum"],
              ["Investment Term", "5 Years (fixed note)"],
              ["Minimum Investment", "£10,000"],
              ["Payment Frequency", "Monthly — every calendar month"],
              ["Currency", "GBP (Sterling)"],
              ["Asset Class", "UK Assisted Living / Supported Housing"],
              ["Security", "First Legal Charge over all underlying properties"],
              ["Security Trustee", "Independent — appointed solely for investor protection"],
              ["Lease Structure", "Full Repairing & Insuring (FRI)"],
              ["Lease Term", "15–25 Years"],
              ["Rent Escalation", "CPI + 1% per annum — your returns rise with inflation"],
              ["Void Periods", "Zero — 100% assured income; no voids since 2006"],
              ["Capital at Maturity", "Repayment subject to underlying asset performance"],
              ["Rollover Option", "Available at maturity (subject to current terms)"],
              ["Regulation", "Section 21 FSMA 2000 financial promotion"],
              ["Exchange Listing", "NASDAQ Dubai (select notes)"],
            ].map(([label, value], i) => (
              <div key={label} className={`flex flex-col sm:flex-row sm:items-center px-6 py-4 gap-1 sm:gap-0 ${i % 2 === 0 ? "bg-white" : "bg-[#F5F3EE]/60"}`}>
                <p className="text-slate-500 text-xs font-medium sm:w-[45%]">{label}</p>
                <p className="text-[#071A2E] text-sm font-semibold sm:w-[55%]">{value}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200 pt-5">
            <p className="text-slate-400 text-[10px]">Thorpe Property Development Ltd (Co. No. 07590563) · One Embankment, Leeds LS1 4DW · Section 21 FSMA 2000 · Capital at risk</p>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          FOUR STEPS
      ══════════════════════════════════════════ */}
      <div id="steps" className="bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-20">
          <h2 className="text-4xl font-bold text-[#071A2E] mb-12" style={{ fontFamily: "'Playfair Display', serif" }}>Four Steps from Commitment to Monthly Income</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            {[
              { num: "01", icon: "ri-user-add-line", title: "You Commit Capital", badge: "£10,000", desc: "From £10,000 you subscribe to the Structured Note. Our team walks you through every form — eligibility, subscription agreement, and source of funds. Most investors are fully onboarded within five working days.", bullets: ["Min. £10,000 — open to Certified HNW & Sophisticated Investors", "Full documentation support provided by our investor relations team", "Funds held securely pending SPV deployment"] },
              { num: "02", icon: "ri-building-2-line", title: "Capital Enters a Ring-Fenced SPV", badge: "First Charge — On every asset", desc: "Your funds are pooled inside a Special Purpose Vehicle — legally separate from Thorpe Dellow's balance sheet. An Independent Security Trustee registers a first legal charge over every property before a single pound is deployed.", bullets: ["Ring-fenced from Thorpe Dellow's own balance sheet", "First charge registered at HM Land Registry on every asset", "Independent Trustee holds security solely for noteholders"] },
              { num: "03", icon: "ri-file-text-line", title: "Long-Term Leases Lock In the Income", badge: "CPI +1%", desc: "Each property is leased to a government-registered housing association on Full Repairing and Insuring terms — zero maintenance cost to the SPV. Rent is reviewed annually at CPI + 1%, contractually locked in for 15–25 years.", bullets: ["FRI terms — SPV bears no maintenance liability", "CPI + 1% annual rent review — returns rise with inflation", "Lease lengths of 15–25 years outlast your 5-year term"] },
              { num: "04", icon: "ri-money-pound-circle-line", title: "Monthly Income Paid to You", badge: "Monthly", desc: "Rent flows from the housing association through the SPV directly into your account — every calendar month for five years. At maturity your capital is returned, with a rollover option available at then-current terms.", bullets: ["Paid monthly — every calendar month without exception", "Fixed 5-year term from your subscription date", "Rollover option at maturity — no need to restart the process"] },
            ].map(s => (
              <div key={s.num} className="bg-[#F5F3EE] rounded-2xl p-7">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#071A2E]">
                      <i className={`${s.icon} text-[#C9A84C] text-lg leading-none`} />
                    </div>
                    <span className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">{s.num}</span>
                  </div>
                  <span className="text-[10px] font-bold text-[#071A2E] bg-[#C9A84C]/20 px-3 py-1 rounded-full whitespace-nowrap">{s.badge}</span>
                </div>
                <h3 className="text-base font-bold text-[#071A2E] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{s.title}</h3>
                <p className="text-slate-600 text-xs leading-relaxed mb-4">{s.desc}</p>
                <div className="flex flex-col gap-2">
                  {s.bullets.map(b => (
                    <div key={b} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-[#C9A84C] text-xs leading-none" />
                      </div>
                      <p className="text-slate-600 text-xs leading-relaxed">{b}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 pt-5">
            <p className="text-slate-300 text-[10px]">Thorpe Property Development Ltd (Co. No. 07590563) · One Embankment, Leeds LS1 4DW · Section 21 FSMA 2000 · Capital at risk</p>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          SECTION 03 — RETURNS & COMPARISON
      ══════════════════════════════════════════ */}
      <div id="returns" className="bg-[#F5F3EE]">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-20">
          <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.3em] mb-3">Section 03</p>
          <h2 className="text-4xl font-bold text-[#071A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Returns &amp; Comparison</h2>
          <p className="text-slate-600 text-sm leading-relaxed mb-10 max-w-2xl">
            Here's the honest truth: most "safe" investments barely keep up with inflation. Savings accounts offer 5%. Gilts give you 4.4%. Meanwhile, Thorpe Dellow investors have been collecting 10% — every year, every month — since 2006.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
            {/* Comparison table */}
            <div>
              <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 mb-3">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[400px]">
                    <thead>
                      <tr className="bg-[#071A2E]">
                        <th className="text-left px-5 py-3 text-white text-xs font-bold uppercase tracking-wide">Product</th>
                        <th className="text-left px-5 py-3 text-white text-xs font-bold uppercase tracking-wide">Return</th>
                        <th className="text-left px-5 py-3 text-white text-xs font-bold uppercase tracking-wide">Security</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { p: "Thorpe Dellow Social Housing Note", r: "10.00%", s: "First Legal Charge", hi: true },
                        { p: "UK Savings Account (Best Rate)", r: "5.00%", s: "FSCS up to £85k", hi: false },
                        { p: "UK Government Gilts (10yr)", r: "4.40%", s: "Sovereign", hi: false },
                        { p: "FTSE 100 (20yr avg)", r: "7.50%", s: "None", hi: false },
                        { p: "UK Buy-to-Let (avg yield)", r: "5.50%", s: "Property", hi: false },
                        { p: "Corporate Bonds (IG avg)", r: "5.80%", s: "Unsecured", hi: false },
                      ].map((row, i) => (
                        <tr key={row.p} className={row.hi ? "bg-[#C9A84C]/8 border-l-4 border-[#C9A84C]" : i % 2 === 0 ? "bg-white" : "bg-[#F5F3EE]/50"}>
                          <td className="px-5 py-3">
                            <p className={`text-xs font-semibold ${row.hi ? "text-[#071A2E]" : "text-slate-600"}`}>{row.p}</p>
                          </td>
                          <td className="px-5 py-3">
                            <p className={`font-bold ${row.hi ? "text-[#C9A84C] text-base" : "text-slate-700 text-xs"}`}>{row.r}</p>
                          </td>
                          <td className="px-5 py-3 text-slate-500 text-xs">{row.s}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="text-slate-400 text-[10px]">Returns are targets only and not guaranteed. FTSE 100 figure reflects 20-year historical average. Capital is at risk.</p>
            </div>

            {/* Live calculator */}
            <ReturnsCalculator />
          </div>

          {/* Illustrative returns table */}
          <h3 className="text-2xl font-bold text-[#071A2E] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>What Could Your Money Earn?</h3>
          <p className="text-slate-600 text-sm leading-relaxed mb-6 max-w-2xl">
            The numbers below show what a consistent 10.00% annual return looks like across a 5-year term. Each pound you invest is working harder than it would anywhere else in the fixed-income world right now.
          </p>
          <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 mb-3">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px]">
                <thead>
                  <tr className="bg-[#F5F3EE]">
                    <th className="text-left px-6 py-3 text-slate-500 text-xs font-bold uppercase tracking-wide">Investment</th>
                    <th className="text-left px-6 py-3 text-slate-500 text-xs font-bold uppercase tracking-wide">Annual Income</th>
                    <th className="text-left px-6 py-3 text-slate-500 text-xs font-bold uppercase tracking-wide">5-Year Income</th>
                    <th className="text-left px-6 py-3 text-slate-500 text-xs font-bold uppercase tracking-wide">Total at Maturity</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["£10,000","£1,000","£5,000","£15,000"],
                    ["£25,000","£2,500","£12,500","£37,500"],
                    ["£50,000","£5,000","£25,000","£75,000"],
                    ["£100,000","£10,000","£50,000","£150,000"],
                    ["£250,000","£25,000","£125,000","£375,000"],
                  ].map(([inv,ann,five,tot],i) => (
                    <tr key={inv} className={i%2===0?"bg-white":"bg-[#F5F3EE]/40"}>
                      <td className="px-6 py-4 text-[#071A2E] font-bold text-sm">{inv}</td>
                      <td className="px-6 py-4 text-[#C9A84C] font-semibold text-sm">{ann}</td>
                      <td className="px-6 py-4 text-slate-600 text-sm">{five}</td>
                      <td className="px-6 py-4 text-[#071A2E] font-bold text-sm">{tot}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-slate-400 text-[10px] mb-6">Illustrative only. Based on 10.00% p.a. target. Returns are not guaranteed and capital may be at risk. Tax treatment depends on your individual circumstances — always seek independent advice.</p>
          <div className="border-t border-slate-200 pt-5">
            <p className="text-slate-400 text-[10px]">Thorpe Property Development Ltd (Co. No. 07590563) · One Embankment, Leeds LS1 4DW · Section 21 FSMA 2000 · Capital at risk · © 2026 Thorpe Dellow</p>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          SECTION 04 — ASSET PORTFOLIO
      ══════════════════════════════════════════ */}
      <div id="portfolio" className="bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-20">
          <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.3em] mb-3">Section 04</p>
          <h2 className="text-4xl font-bold text-[#071A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>The Asset Portfolio</h2>
          <p className="text-slate-600 text-sm leading-relaxed mb-10 max-w-2xl">
            You don't have to take our word for it. The portfolio behind your investment is real, tangible, and spread across three nations. Every property is leased to a government-registered housing association — and not one has gone dark since 2006.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
            {[
              { v: "35", l: "Properties", sub: "Across ENG, WAL & SCO" },
              { v: "£47M+", l: "Combined AUM", sub: "Assets under management" },
              { v: "545+", l: "Residential Units", sub: "Beds and care units" },
              { v: "0", l: "Void Periods", sub: "Since 2006 — zero" },
            ].map(s => (
              <div key={s.l} className="bg-[#071A2E] rounded-2xl p-6 text-center">
                <p className="text-[#C9A84C] text-3xl md:text-4xl font-bold leading-none mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{s.v}</p>
                <p className="text-white text-xs font-semibold mt-1">{s.l}</p>
                <p className="text-white/40 text-[10px] mt-0.5">{s.sub}</p>
              </div>
            ))}
          </div>
          <div className="bg-[#F5F3EE] rounded-2xl p-8 mb-14">
            <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.3em] mb-3">The Market Opportunity</p>
            <p className="text-[#071A2E] text-sm leading-relaxed mb-6 max-w-2xl">The gap between demand and supply in UK social housing is not closing — it's widening. That structural shortage underpins every lease in this portfolio.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { v: "1.2M", l: "People on UK waiting lists" },
                { v: "£12.6B", l: "Annual housing benefit payment" },
                { v: "300K", l: "Unit shortfall built each year" },
                { v: "CPI+1%", l: "Annual rent escalation built in" },
              ].map(s => (
                <div key={s.l} className="bg-white rounded-xl p-5 text-center border border-slate-100">
                  <p className="text-[#C9A84C] text-2xl font-bold leading-none mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{s.v}</p>
                  <p className="text-slate-500 text-[10px] leading-snug">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
          <h3 className="text-2xl font-bold text-[#071A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Portfolio Breakdown</h3>
          <div className="flex flex-col gap-5 mb-14">
            {[
              { icon: "ri-heart-pulse-line", title: "Assisted Living", count: "17 Properties", desc: "Purpose-built properties for adults with care needs. Long-term government-funded occupancy, FRI leases, and the highest rental yields in the social housing sector." },
              { icon: "ri-community-line", title: "Supported Housing", count: "12 Properties", desc: "Accommodation for adults with mental health, learning disabilities, or addiction recovery needs. Fully government-funded through housing benefit. Zero voids since 2006." },
              { icon: "ri-home-heart-line", title: "Sheltered Housing", count: "6 Properties", desc: "Independent living for older adults with on-site support. Stable, long-term tenancies underpinned by local authority and NHS commissioning." },
            ].map(pt => (
              <div key={pt.title} className="bg-[#F5F3EE] rounded-2xl p-6 flex items-start gap-5">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#071A2E] flex-shrink-0">
                  <i className={`${pt.icon} text-[#C9A84C] text-xl leading-none`} />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h4 className="text-base font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>{pt.title}</h4>
                    <span className="text-[10px] font-bold text-[#C9A84C] bg-[#C9A84C]/12 px-3 py-1 rounded-full uppercase tracking-wide">{pt.count}</span>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">{pt.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <h3 className="text-2xl font-bold text-[#071A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Why UK Social Housing?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            {[
              { num: "01", icon: "ri-government-line", title: "The UK Cannot Build Fast Enough", desc: "1.2 million people are on UK social housing waiting lists and the government has missed its housebuilding target every year since it was set. That gap widens every year — and every Thorpe Dellow property sits squarely inside it.", bullets: ["1.2M+ people on UK housing waiting lists today", "Structural undersupply drives consistent, growing demand"] },
              { num: "02", icon: "ri-shield-check-line", title: "Recession-Proof by Design", desc: "In 2008 and again in 2020, housing benefit did not miss a single payment. Social housing demand rises in downturns as private tenants lose affordability — the exact opposite of commercial property. We have never had a void.", bullets: ["100% occupancy through 2008, 2020, and every cycle since", "Housing benefit is a statutory obligation — not discretionary spend"] },
              { num: "03", icon: "ri-time-line", title: "25-Year Leases Outlast Your Investment", desc: "Properties are let on Full Repairing and Insuring terms — the tenant carries all maintenance costs. Leases run 15–25 years with annual CPI + 1% rent reviews, extending well beyond your 5-year term.", bullets: ["FRI terms — zero maintenance liability for the SPV", "CPI + 1% annual uplift — returns rise automatically with inflation"] },
            ].map(w => (
              <div key={w.title} className="bg-[#F5F3EE] rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#071A2E]">
                    <i className={`${w.icon} text-[#C9A84C] text-base leading-none`} />
                  </div>
                  <span className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">{w.num}</span>
                </div>
                <h4 className="text-sm font-bold text-[#071A2E] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{w.title}</h4>
                <p className="text-slate-500 text-xs leading-relaxed mb-3">{w.desc}</p>
                {w.bullets.map(b => (
                  <div key={b} className="flex items-start gap-2 mb-1">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-check-line text-[#C9A84C] text-xs leading-none" /></div>
                    <p className="text-slate-500 text-xs">{b}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 pt-5">
            <p className="text-slate-300 text-[10px]">Thorpe Property Development Ltd (Co. No. 07590563) · One Embankment, Leeds LS1 4DW · Section 21 FSMA 2000 · Capital at risk</p>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          SECTION 05 — SECURITY & STRUCTURE
      ══════════════════════════════════════════ */}
      <div id="security" className="bg-[#071A2E]">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-20">
          <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.3em] mb-3">Section 05</p>
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Security &amp; Structure</h2>
          <p className="text-slate-400 text-sm leading-relaxed mb-12 max-w-2xl">The most important question any investor should ask isn't "what's the return?" It's "what happens if something goes wrong?" Here's our answer.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
            {[
              { icon: "ri-shield-check-line", title: "First Legal Charge", desc: "A first legal charge over each underlying property is registered at HM Land Registry and held by an Independent Security Trustee — not by Thorpe Dellow. In any default scenario, your claim ranks ahead of the company's own creditors." },
              { icon: "ri-building-2-line", title: "Ring-Fenced SPV", desc: "Your capital sits inside a Special Purpose Vehicle that is legally separate from Thorpe Dellow's operating balance sheet. Our operational liabilities cannot reach your capital." },
              { icon: "ri-user-star-line", title: "Independent Security Trustee", desc: "The Trustee is empowered to enforce charges on your behalf — without requiring your instruction or Thorpe Dellow's consent. They work exclusively for noteholders." },
              { icon: "ri-government-line", title: "Government-Backed Income", desc: "Every lease is underpinned by housing benefit — a statutory government obligation, not a discretionary budget line. It cannot be frozen, withdrawn, or deferred by market conditions." },
            ].map(f => (
              <div key={f.title} className="bg-white/6 border border-white/10 rounded-2xl p-6 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C9A84C]/15 flex-shrink-0">
                  <i className={`${f.icon} text-[#C9A84C] text-base leading-none`} />
                </div>
                <div>
                  <h4 className="text-white text-sm font-bold mb-2">{f.title}</h4>
                  <p className="text-slate-400 text-xs leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 mb-10">
            <h3 className="text-base font-bold text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Lease Types in the Portfolio</h3>
            <div className="flex flex-col gap-5">
              {[
                { type: "Full Repairing & Insuring (FRI)", pct: "100%", desc: "All maintenance, repairs, and insurance obligations fall on the housing association tenant — not the SPV." },
                { type: "Government Housing Association Tenants", pct: "100%", desc: "Every tenant is a UK-registered housing association, providing institutional-grade counterparty quality." },
                { type: "CPI + 1% Annual Rent Review", pct: "100%", desc: "Annual rent escalation contractually locked in across the entire portfolio." },
              ].map(l => (
                <div key={l.type} className="flex flex-col sm:flex-row sm:items-start gap-3 border-b border-white/8 pb-5 last:border-0 last:pb-0">
                  <div className="sm:w-2/5">
                    <p className="text-white text-sm font-semibold">{l.type}</p>
                    <p className="text-[#C9A84C] text-xs font-bold mt-0.5">{l.pct} of portfolio</p>
                  </div>
                  <p className="text-slate-400 text-xs leading-relaxed sm:w-3/5">{l.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#C9A84C]/10 border border-[#C9A84C]/20 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C9A84C]/20">
                <i className="ri-question-line text-[#C9A84C] text-base leading-none" />
              </div>
              <h3 className="text-white font-bold text-base">What Happens If Something Goes Wrong?</h3>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">The Independent Security Trustee holds first legal charges over every property and is empowered to enforce those charges on your behalf — without requiring your instruction or Thorpe Dellow's consent. The SPV ring-fence means our operational liabilities cannot reach your capital. These structures were built for worst-case scenarios. We've never needed them. But they're there.</p>
          </div>
          <div className="border-t border-white/10 pt-6 mt-10">
            <p className="text-white/20 text-[10px]">Thorpe Property Development Ltd (Co. No. 07590563) · One Embankment, Leeds LS1 4DW · Section 21 FSMA 2000 · Capital at risk</p>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          SECTION 06 — THE TEAM
      ══════════════════════════════════════════ */}
      <div id="team" className="bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-20">
          <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.3em] mb-3">Section 06</p>
          <h2 className="text-4xl font-bold text-[#071A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>The Team Behind the Track Record</h2>
          <p className="text-slate-600 text-sm leading-relaxed mb-12 max-w-2xl">Every great investment starts with great people. The Thorpe Dellow team has been doing this work — together, in the same building, with the same conviction — for over two decades.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {[
              { name: "James Thorpe", role: "Founder & Chief Executive", bio: "Founded Thorpe Dellow in Leeds in 2005. Over 20 years building and managing UK social housing investment structures.", img: "https://readdy.ai/api/search-image?query=professional%20British%20male%20executive%20portrait%2050s%20confident%20dark%20suit%20white%20shirt%20clean%20neutral%20studio%20background%20high%20quality%20corporate%20headshot%20photography%20warm%20lighting%20sharp%20focus&width=300&height=360&seq=tm001&orientation=portrait" },
              { name: "Sarah Dellow", role: "Co-Founder & Investment Director", bio: "Co-founded Thorpe Dellow in 2005. Leads all investor relations and structured product origination. Chartered accountant with 18 years in alternative investments.", img: "https://readdy.ai/api/search-image?query=professional%20British%20female%20executive%20portrait%2040s%20confident%20dark%20blazer%20clean%20neutral%20studio%20background%20high%20quality%20corporate%20headshot%20photography%20warm%20lighting%20sharp%20focus&width=300&height=360&seq=tm002&orientation=portrait" },
              { name: "Michael Hargreaves", role: "Head of Asset Management", bio: "Oversees day-to-day management of all 35 properties. Former Director of Housing at a major UK local authority with 15 years in social housing operations.", img: "https://readdy.ai/api/search-image?query=professional%20British%20male%20property%20manager%20portrait%2045s%20smart%20casual%20neutral%20studio%20background%20high%20quality%20corporate%20headshot%20photography%20warm%20lighting%20sharp%20focus&width=300&height=360&seq=tm003&orientation=portrait" },
              { name: "Rachel Okafor", role: "Head of Compliance & Legal", bio: "Qualified solicitor specialising in structured finance and financial promotions. Ensures all products comply with FSMA 2000 and FCA guidelines.", img: "https://readdy.ai/api/search-image?query=professional%20British%20female%20lawyer%20portrait%2038s%20smart%20suit%20neutral%20studio%20background%20high%20quality%20corporate%20headshot%20photography%20warm%20lighting%20sharp%20focus&width=300&height=360&seq=tm004&orientation=portrait" },
            ].map(m => (
              <div key={m.name} className="bg-[#F5F3EE] rounded-2xl overflow-hidden">
                <div className="w-full h-56 overflow-hidden">
                  <img src={m.img} alt={m.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-5">
                  <h4 className="font-bold text-[#071A2E] text-sm mb-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>{m.name}</h4>
                  <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-wide mb-3">{m.role}</p>
                  <p className="text-slate-500 text-xs leading-relaxed">{m.bio}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 pt-5">
            <p className="text-slate-300 text-[10px]">Thorpe Property Development Ltd (Co. No. 07590563) · One Embankment, Leeds LS1 4DW · Section 21 FSMA 2000 · Capital at risk</p>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          SECTION 07 — SPEAK WITH OUR TEAM
      ══════════════════════════════════════════ */}
      <div id="contact" className="bg-[#F5F3EE]">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-20">
          <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.3em] mb-3">Section 07</p>
          <h2 className="text-4xl font-bold text-[#071A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Speak With Our Team</h2>
          <p className="text-slate-600 text-sm leading-relaxed mb-12 max-w-2xl">The best investment decisions aren't made on paper alone. They start with a conversation. Our team is here Monday to Friday, ready to walk you through every detail — no pressure, no obligation.</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-14">
            <div className="flex flex-col gap-5">
              {[
                { icon: "ri-map-pin-2-line", label: "ADDRESS", value: "One Embankment, Leeds LS1 4DW\nUnited Kingdom" },
                { icon: "ri-phone-line", label: "PHONE", value: "+44 20 3097 6185 (UK) · +1 437 292 2296 (International)" },
                { icon: "ri-mail-line", label: "EMAIL", value: "invest@thorpedellow.com" },
                { icon: "ri-global-line", label: "WEB", value: "portal.thorpedellow.com · thorpedellow.com" },
                { icon: "ri-time-line", label: "HOURS", value: "Monday to Friday, 9:00am – 5:00pm GMT" },
              ].map(c => (
                <div key={c.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#071A2E] flex-shrink-0">
                    <i className={`${c.icon} text-[#C9A84C] text-base leading-none`} />
                  </div>
                  <div>
                    <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-widest mb-0.5">{c.label}</p>
                    <p className="text-[#071A2E] text-sm font-semibold leading-relaxed whitespace-pre-line">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#071A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Six Reasons Our Investors Stay</h3>
              <div className="flex flex-col gap-3">
                {[
                  { num: "01", title: "10.00% p.a. — Real Returns", desc: "Not a theoretical ceiling. The target we've been delivering, year in, year out, since 2006." },
                  { num: "02", title: "Monthly — Not Quarterly", desc: "Your money works for you every 30 days — credited to your account, not held until term end." },
                  { num: "03", title: "Ranked Ahead in Any Default", desc: "The Trustee can enforce charges without your instruction or ours. Your claim is first in line." },
                  { num: "04", title: "Zero Voids in 19 Years", desc: "Every unit occupied. Every lease producing income. Not one empty property since 2006." },
                  { num: "05", title: "Built for the Long Term", desc: "Our structures, our leases, and our relationships with housing associations predate your note by years." },
                  { num: "06", title: "Demand That Doesn't Switch Off", desc: "There is no economic scenario where the UK needs fewer social homes. That's the bedrock this portfolio is built on." },
                ].map(r => (
                  <div key={r.num} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-slate-100">
                    <span className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest flex-shrink-0 mt-0.5 w-6">{r.num}</span>
                    <div>
                      <p className="text-[#071A2E] text-xs font-bold mb-0.5">{r.title}</p>
                      <p className="text-slate-500 text-xs leading-relaxed">{r.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Ready to make your money work harder? */}
          <div className="bg-[#071A2E] rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-8 mb-10">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Ready to make your money work harder?</h3>
              <p className="text-slate-400 text-sm leading-relaxed max-w-xl">
                Call <strong className="text-white">+44 20 3097 6185</strong> or email <strong className="text-white">invest@thorpedellow.com</strong> to speak with an investment specialist.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-bold text-sm text-[#071A2E] cursor-pointer whitespace-nowrap transition-all hover:scale-105"
                style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E2C27A 50%, #C9A84C 100%)" }}
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line leading-none" /></div>
                Express Interest
              </button>
              <a href="tel:+442030976185" className="flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-bold text-sm text-white border border-white/20 cursor-pointer whitespace-nowrap hover:bg-white/8 transition-colors">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line leading-none" /></div>
                Call Now
              </a>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-5">
            <p className="text-slate-400 text-[10px]">Thorpe Property Development Ltd (Co. No. 07590563) · One Embankment, Leeds LS1 4DW · Section 21 FSMA 2000 · Capital at risk · © 2026 Thorpe Dellow</p>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          LEGAL NOTICE — PAGE 11
      ══════════════════════════════════════════ */}
      <div className="bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-14">
          <div className="border border-slate-200 rounded-2xl p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 flex-shrink-0">
                <i className="ri-error-warning-line text-amber-600 text-base leading-none" />
              </div>
              <h3 className="text-base font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>Important Legal Notice</h3>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed mb-5">
              Investments are speculative and involve significant risk. Investors may lose some or all of their capital. These products are not protected by the FSCS. Target returns are illustrative only and are not guaranteed. Past performance is not indicative of future results. Tax treatment depends on individual circumstances — seek independent advice. This document does not constitute financial, investment, legal, or tax advice.
            </p>
            <p className="text-slate-600 text-sm leading-relaxed mb-5">
              This document is a financial promotion issued in accordance with Section 21 of the Financial Services and Markets Act 2000 (FSMA). It is directed solely at Certified High Net Worth Individuals and Self-Certified Sophisticated Investors. Thorpe Property Development Ltd (Company No. 07590563), trading as Thorpe Dellow. Registered in England and Wales. Registered office: One Embankment, Leeds LS1 4DW, United Kingdom.
            </p>
            <div className="border-t border-slate-100 pt-5">
              <p className="text-slate-400 text-[10px]">Thorpe Property Development Ltd (Co. No. 07590563) · One Embankment, Leeds LS1 4DW · Section 21 FSMA 2000 · Capital at risk · © 2026 Thorpe Dellow</p>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          BACK COVER
      ══════════════════════════════════════════ */}
      <div className="bg-[#071A2E] relative overflow-hidden">
        <div className="h-[3px] w-full" style={{ background: "linear-gradient(90deg, transparent, #C9A84C 30%, #E8C56A 50%, #C9A84C 70%, transparent)" }} />
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-14 flex flex-col md:flex-row items-center justify-between gap-10">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl border border-[#C9A84C]/40" style={{ background: "rgba(201,168,76,0.12)" }}>
                <i className="ri-home-4-fill text-[#C9A84C] text-lg leading-none" />
              </div>
              <div>
                <p className="font-bold text-sm tracking-widest uppercase text-white leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>Thorpe Dellow</p>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#C9A84C]/70 mt-0.5">Structured Investments</p>
              </div>
            </div>
            <p className="text-white/30 text-xs leading-relaxed max-w-md">
              Thorpe Property Development Ltd (Co. No. 07590563) · One Embankment, Leeds LS1 4DW · Section 21 FSMA 2000 · Capital at risk · © 2026 Thorpe Dellow
            </p>
          </div>
          <div className="flex flex-col items-center md:items-end gap-2 flex-shrink-0">
            <p className="text-[#C9A84C] text-5xl font-bold leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>10.00%</p>
            <p className="text-white/50 text-xs">Target Return p.a.</p>
            <div className="w-16 h-px bg-white/15 my-2" />
            <p className="text-white text-sm font-semibold">thorpedellow.com</p>
            <p className="text-white/40 text-xs">invest@thorpedellow.com</p>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { margin: 0; size: A4; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
